import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Maximize4Arrows from '@/images/Maximize4Arrows.svg';
import { ControlRow } from '@/pages/AdvancedEditor/components/panels/common/ControlRow';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { usePresenterDrivers } from '@/pages/AdvancedEditor/hooks/usePresenterDrivers';
import { getAvatarElementFromPage, getAvatarGroupFromPage, getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { VideoType } from '@d-id/types';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { ImageElementType } from 'polotno/model/image-model';
import { VideoElementType } from 'polotno/model/video-model';
import { fitToPageElement } from '../../../elements/actions/FitToPage';
import { ControlButton } from '../../common/ControlButton';
import {
    AvatarControl,
    AvatarMaskingCircleControls,
    EmotionsControl,
    MovementsControl,
    PositionControl,
    SentimentControl,
    ViewControl,
} from './controls';

const ModifyPresenterContainer = styled.div`
    padding-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const PresenterVisualsContainer = styled.div`
    padding: 20px;
    border: 1px solid ${({ theme }) => theme.colors.black[200]};
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

interface Props {
    setEditMode: (editMode: boolean) => void;
}

export const ModifyPresenter = observer(({ setEditMode }: Props) => {
    const theme = useTheme();
    const { store } = useAdvancedEditorContext();
    const avatarPresenter = getAvatarPresenterFromPage(store.activePage);
    const { presenterDrivers } = usePresenterDrivers(avatarPresenter);
    const mixpanel = useAnalytics();
    const avatarMaskingEnabled = useFlag('avatar_masking_enabled', false);
    const sentimentControlEnabled = useFlag('expressive_avatars', false);

    const showEmotions = avatarPresenter && ![VideoType.CLIP, VideoType.Scene, VideoType.EXPRESSIVE].includes(avatarPresenter?.type);

    const handleFitToPage = async () => {
        const avatarElement = getAvatarElementFromPage(store.activePage);

        if (!avatarElement) {
            return;
        }

        const group = getAvatarGroupFromPage(store.activePage);

        const modifiedAvatarElement = await fitToPageElement(avatarElement as ImageElementType | VideoElementType);

        if (group) {
            return group.children.forEach((child: ElementType) =>
                child.set({
                    x: modifiedAvatarElement.x,
                    y: modifiedAvatarElement.y,
                    width: modifiedAvatarElement.width,
                    height: modifiedAvatarElement.height,
                })
            );
        }

        mixpanel?.track('fit-to-page', {
            from: 'contextual-side-panel',
            'element-type': 'avatar',
            event: 'click',
        });
    };

    return (
        <ModifyPresenterContainer data-testid="modify-presenter-container">
            <PresenterVisualsContainer>
                <AvatarControl setEditMode={setEditMode} />

                {sentimentControlEnabled && <SentimentControl />}

                {avatarMaskingEnabled && (
                    <>
                        <ViewControl />
                        <AvatarMaskingCircleControls />
                    </>
                )}

                {showEmotions && (
                    <ControlRow>
                        Emotions
                        <EmotionsControl />
                    </ControlRow>
                )}

                {presenterDrivers.length > 1 && (
                    <ControlRow>
                        Movements
                        <MovementsControl />
                    </ControlRow>
                )}

                <ControlRow>
                    Position
                    <PositionControl />
                </ControlRow>

                <ControlRow>
                    Fit to page
                    <ControlButton onClick={handleFitToPage} selected={false}>
                        <MaskedIcon src={Maximize4Arrows} size="20px" color={theme.colors.grey[500]} />
                    </ControlButton>
                </ControlRow>
            </PresenterVisualsContainer>
        </ModifyPresenterContainer>
    );
});
